import { useEffect, useState, type ReactNode } from 'react';
import { ExternalLink, FileText, PenLine, RotateCcw, Save, Send, X } from 'lucide-react';
import { contentActionAllowed, sanitizeArticleHtml, type ContentAction } from '@cp/shared';
import { Banner, Button, ButtonLink, Drawer, Elapsed, Fact, Facts, Field, Input, Spinner, StatusPill, Steps, SubTabs, type StepItem } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { useContentAction, useContentItem, useEditContent } from '@/hooks/useContent';
import { toast } from '@/store/toastStore';
import { CONTENT_ORIGIN_LABELS, CONTENT_STATE, formatUsd } from '@/utils/content';
import { errorField, errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import type { ContentItemView } from '@/types/api';

type View = 'preview' | 'edit';

/** An article under way, or one whose way ended in a failure: the steps are worth showing. */
const showsProgress = (item: ContentItemView) =>
  item.state === 'queued' || item.state === 'writing' || item.state === 'publishing' || item.state === 'failed' || (item.state === 'ready' && item.publishAs !== null);

/**
 * An article's way as a list (ADR-0053): the queue, Claude's search for
 * material, the writing — with the time it has taken — and, where the profile
 * sends it on, the CMS.
 */
function articleSteps(item: ContentItemView): StepItem[] {
  const writing = item.state === 'writing';
  const written = item.html !== null && item.html !== undefined && item.state !== 'queued' && item.state !== 'writing';
  const failedWriting = item.state === 'failed' && !written;
  const elapsed = writing && item.startedAt ? <Elapsed since={item.startedAt} /> : null;
  const steps: StepItem[] = [
    { label: 'Очередь', state: item.state === 'queued' ? 'running' : 'done', detail: item.state === 'queued' ? 'Статьи пишутся по одной, очередь проверяется каждую минуту' : undefined },
    {
      label: 'Поиск материала',
      state: writing ? (item.stage === 'writing' ? 'done' : 'running') : item.state === 'queued' ? 'pending' : failedWriting ? 'failed' : 'done',
      detail: writing && item.stage !== 'writing' ? <>Claude ищет свежие источники · {elapsed}</> : undefined,
    },
    {
      label: 'Написание',
      state: writing ? (item.stage === 'writing' ? 'running' : 'pending') : written ? 'done' : failedWriting ? 'failed' : 'pending',
      detail: writing && item.stage === 'writing' ? <>Claude пишет текст · {elapsed}</> : item.words ? `${item.words.toLocaleString('ru-RU')} слов` : undefined,
    },
  ];
  if (item.publishAs || item.state === 'publishing' || item.state === 'published' || (item.state === 'failed' && written)) {
    steps.push({
      label: item.publishAs === 'draft' ? 'Черновик в WordPress' : 'Публикация',
      state: item.state === 'publishing' ? 'running' : item.state === 'published' ? 'done' : item.state === 'failed' ? 'failed' : 'pending',
      detail: item.state === 'ready' && item.publishAt ? `По графику ${formatRelative(item.publishAt)}` : item.state === 'publishing' ? 'Отправляется в CMS' : undefined,
    });
  } else if (written) {
    steps.push({ label: 'Проверка редактором', state: 'running', detail: 'Опубликуйте или отправьте черновиком' });
  }
  return steps;
}
type ContentItemAction = Exclude<ContentAction, 'edit'>;

/**
 * One article (ADR-0051): read as it will be published, changed, and sent on
 * — published, sent to WordPress as a draft, written again or rejected.
 */
export function ContentItemDrawer({ id, mayManage, onClose }: { readonly id: string; readonly mayManage: boolean; readonly onClose: () => void }): ReactNode {
  const item = useContentItem(id);
  const [view, setView] = useState<View>('preview');
  const [title, setTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [tags, setTags] = useState('');
  const [category, setCategory] = useState('');
  const [keyword, setKeyword] = useState('');
  const [brief, setBrief] = useState('');
  const [html, setHtml] = useState('');

  const data = item.data;
  useEffect(() => {
    if (!data) return;
    setTitle(data.title);
    setMetaDescription(data.metaDescription ?? '');
    setExcerpt(data.excerpt ?? '');
    setTags(data.tags.join(', '));
    setCategory(data.category ?? '');
    setKeyword(data.keyword ?? '');
    setBrief(data.brief ?? '');
    setHtml(data.html ?? '');
  }, [data]);

  const act = useContentAction({
    onSuccess: (_, variables) => {
      toast.success(DONE[variables.action] ?? 'Готово');
      if (variables.action === 'reject') onClose();
    },
    onError: (error) =>
      toast.error(
        'Не удалось',
        errorField(error) === 'credentialId'
          ? 'Сначала выберите доступ к CMS в профиле контента'
          : errorField(error) === 'platform'
            ? 'Публикация из панели пока только в WordPress'
            : errorText(error),
      ),
  });
  const edit = useEditContent(id, {
    onSuccess: () => {
      toast.success('Сохранено');
      setView('preview');
    },
    onError: (error) => toast.error('Не удалось сохранить', errorText(error)),
  });

  const allowed = (action: ContentAction) => mayManage && data !== undefined && contentActionAllowed(action, data.state);
  const run = (action: ContentItemAction, body?: Record<string, unknown>) => act.mutate({ id, action, ...(body ? { body } : {}) });
  const saveEdits = () =>
    edit.mutate(
      data?.state === 'idea'
        ? { title, keyword: keyword.trim() || null, brief: brief.trim() || null }
        : {
            title,
            metaDescription: metaDescription.trim() || null,
            excerpt: excerpt.trim() || null,
            tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
            category: category.trim() || null,
            html,
          },
    );

  return (
    <Drawer
      open
      width="lg"
      title={data?.title ?? 'Статья'}
      {...(data ? { description: `${CONTENT_ORIGIN_LABELS[data.origin] ?? data.origin} · ${formatRelative(data.createdAt)}` } : {})}
      onClose={onClose}
      footer={
        data ? (
          <div className="row row-wrap">
            {view === 'edit' ? (
              <>
                <Button variant="ghost" onClick={() => setView('preview')}>
                  Отмена
                </Button>
                <Button variant="primary" icon={Save} busy={edit.isPending} onClick={saveEdits}>
                  Сохранить
                </Button>
              </>
            ) : (
              <>
                {allowed('write') ? (
                  <Button variant="primary" icon={PenLine} busy={act.isPending} onClick={() => run('write')}>
                    Написать
                  </Button>
                ) : null}
                {allowed('publish') ? (
                  <>
                    <Button variant="primary" icon={Send} busy={act.isPending} onClick={() => run('publish', { as: 'publish' })}>
                      Опубликовать
                    </Button>
                    <Button icon={FileText} busy={act.isPending} onClick={() => run('publish', { as: 'draft' })}>
                      Черновиком в WordPress
                    </Button>
                  </>
                ) : null}
                {allowed('retry') ? (
                  <Button variant="primary" icon={RotateCcw} busy={act.isPending} onClick={() => run('retry')}>
                    Повторить
                  </Button>
                ) : null}
                {allowed('rewrite') ? (
                  <Button icon={RotateCcw} busy={act.isPending} onClick={() => run('rewrite')}>
                    Переписать
                  </Button>
                ) : null}
                {allowed('cancel') ? (
                  <Button variant="ghost" busy={act.isPending} onClick={() => run('cancel')}>
                    Убрать из очереди
                  </Button>
                ) : null}
                {allowed('reject') ? (
                  <Button variant="ghost" icon={X} busy={act.isPending} onClick={() => run('reject')}>
                    Отклонить
                  </Button>
                ) : null}
                {data.url ? (
                  <ButtonLink href={data.url} icon={ExternalLink}>
                    Открыть на сайте
                  </ButtonLink>
                ) : null}
              </>
            )}
          </div>
        ) : null
      }
    >
      {item.isPending ? (
        <Spinner />
      ) : item.isError ? (
        <ErrorState error={item.error} />
      ) : data ? (
        <div className="stack">
          <Facts>
            <Fact label="Состояние">
              <StatusPill map={CONTENT_STATE} status={data.state} />
              {data.publishAt && data.state === 'ready' && data.publishAs === 'publish' ? <span className="muted"> · публикация {formatRelative(data.publishAt)}</span> : null}
            </Fact>
            {data.keyword ? <Fact label="Ключевое слово">{data.keyword}</Fact> : null}
            {data.words ? <Fact label="Длина">{data.words.toLocaleString('ru-RU')} слов</Fact> : null}
            {data.costUsd !== null ? (
              <Fact label="Стоимость">
                {formatUsd(data.costUsd)}
                {data.usage ? <span className="muted"> · {data.usage.model} · поисков {data.usage.webSearches}</span> : null}
              </Fact>
            ) : null}
            {data.publishedAt ? <Fact label="В CMS">{`${data.publishAs === 'publish' ? 'опубликована' : 'черновик'} ${formatRelative(data.publishedAt)}`}</Fact> : null}
          </Facts>
          {data.error ? <Banner tone="danger">{data.error}</Banner> : null}
          {showsProgress(data) ? <Steps items={articleSteps(data)} label="Ход работы над статьёй" /> : null}

          {data.state === 'idea' || data.state === 'queued' || data.state === 'writing' || !data.html ? (
            view === 'edit' ? (
              <div className="stack">
                <Field label="Заголовок" htmlFor="idea-title">
                  <Input id="idea-title" value={title} onChange={(event) => setTitle(event.target.value)} />
                </Field>
                <Field label="Ключевое слово" htmlFor="idea-keyword">
                  <Input id="idea-keyword" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
                </Field>
                <Field label="О чём статья" htmlFor="idea-brief">
                  <textarea id="idea-brief" className="input" rows={5} value={brief} onChange={(event) => setBrief(event.target.value)} />
                </Field>
              </div>
            ) : (
              <>
                {data.brief ? <p>{data.brief}</p> : <p className="muted">Без описания</p>}
                {allowed('edit') ? (
                  <div>
                    <Button size="sm" icon={PenLine} onClick={() => setView('edit')}>
                      Изменить тему
                    </Button>
                  </div>
                ) : null}
              </>
            )
          ) : (
            <>
              <SubTabs
                prefix="article"
                label="Статья"
                active={view}
                onChange={setView}
                tabs={[
                  { id: 'preview', label: 'Статья' },
                  ...(allowed('edit') ? [{ id: 'edit' as const, label: 'Правка' }] : []),
                ]}
              />
              {view === 'preview' ? (
                <div className="stack">
                  <Facts>
                    {data.metaDescription ? <Fact label="Meta description">{data.metaDescription}</Fact> : null}
                    {data.tags.length > 0 ? <Fact label="Метки">{data.tags.join(', ')}</Fact> : null}
                    {data.category ? <Fact label="Рубрика">{data.category}</Fact> : null}
                    {data.slug ? <Fact label="Адрес">{`/${data.slug}/`}</Fact> : null}
                  </Facts>
                  {/* Cleaned on the server when written and again here; the page's CSP runs no inline script regardless. */}
                  <article className="article-preview" dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(data.html ?? '') }} />
                </div>
              ) : (
                <div className="stack">
                  <Field label="Заголовок" htmlFor="article-title">
                    <Input id="article-title" value={title} onChange={(event) => setTitle(event.target.value)} />
                  </Field>
                  <Field label="Meta description" htmlFor="article-description" hint={`${metaDescription.length} символов`}>
                    <textarea id="article-description" className="input" rows={2} value={metaDescription} onChange={(event) => setMetaDescription(event.target.value)} />
                  </Field>
                  <Field label="Анонс" htmlFor="article-excerpt">
                    <textarea id="article-excerpt" className="input" rows={2} value={excerpt} onChange={(event) => setExcerpt(event.target.value)} />
                  </Field>
                  <div className="form-grid">
                    <Field label="Метки" htmlFor="article-tags" hint="Через запятую">
                      <Input id="article-tags" value={tags} onChange={(event) => setTags(event.target.value)} />
                    </Field>
                    <Field label="Рубрика" htmlFor="article-category">
                      <Input id="article-category" value={category} onChange={(event) => setCategory(event.target.value)} />
                    </Field>
                  </div>
                  <Field label="Текст (HTML)" htmlFor="article-html">
                    <textarea id="article-html" className="input mono" rows={18} value={html} onChange={(event) => setHtml(event.target.value)} spellCheck={false} />
                  </Field>
                </div>
              )}
            </>
          )}

          {data.sources.length > 0 && view === 'preview' ? (
            <Facts>
              <Fact label="Источники">
                <ul className="list-plain">
                  {data.sources.map((source) => (
                    <li key={source.url} className="truncate">
                      <a href={source.url} target="_blank" rel="noreferrer noopener">
                        {source.title ?? source.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </Fact>
            </Facts>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}

const DONE: Record<string, string> = {
  write: 'Статья поставлена в очередь',
  cancel: 'Убрана из очереди',
  reject: 'Статья отклонена',
  rewrite: 'Статья будет написана заново',
  publish: 'Статья отправлена в WordPress',
  retry: 'Повтор запущен',
};
